import { apiPath } from '../../constants';
import {
    axios,
    apiRequestOptions,
    competitionWhenWhere,
    errorRequestHandler,
    transformHttpResponse,
} from 'utils';
import { addSuccessMessage } from '../SnackBar';

/*
 * THE SUPER ADMIN'S EXCEPTIONS to the FSKA / MSAC split — «Доступи»
 * (docs/architecture/discipline-access.md, stage 5). One screen reads and
 * writes them, so they live in that screen's state, not the store: every
 * action resolves its result (null on failure, the error already shown).
 */

const failed = dispatch => error => {
    dispatch(errorRequestHandler(error));

    return null;
};

export const fetchAccessGrants = () => dispatch =>
    axios(apiRequestOptions(apiPath.ACCESS_GRANTS.LIST))
        .then(transformHttpResponse)
        .then(response => response.data || [])
        .catch(error => transformHttpResponse(error.response))
        .catch(failed(dispatch));

export const postAccessGrant = data => dispatch =>
    axios(apiRequestOptions(apiPath.ACCESS_GRANTS.POST, data))
        .then(transformHttpResponse)
        .then(response => {
            dispatch(addSuccessMessage('Access granted'));

            return response.data;
        })
        .catch(error => transformHttpResponse(error.response))
        .catch(failed(dispatch));

/**
 * An edit, a deactivation or an activation (31 Oct 2026: nothing deletes a
 * grant any more) — the effect, the reason, «Діє до», `active`.
 */
export const patchAccessGrant = (id, data) => dispatch => {
    const params = [];
    params['id'] = id;

    return axios(apiRequestOptions(apiPath.ACCESS_GRANTS.PATCH, data, { params }))
        .then(transformHttpResponse)
        .then(response => {
            dispatch(addSuccessMessage('Access updated'));

            return response.data;
        })
        .catch(error => transformHttpResponse(error.response))
        .catch(failed(dispatch));
};

/**
 * THE LOG OF «ДОСТУПИ» (31 Oct 2026, organiser: «добав лог, хто що робив,
 * хто коли що змінював у доступах») — the grants' business events on the
 * one audit timeline, newest first.
 */
export const fetchAccessGrantLog = () => dispatch =>
    axios(apiRequestOptions(apiPath.ENTITY_CHANGE_LOGS.LIST, {
        entityType: 'AccessGrant',
        entryType: 'business_event',
        itemsPerPage: 500,
        'order[changedAt]': 'desc',
    }))
        .then(transformHttpResponse)
        .then(response => response.data || [])
        .catch(error => transformHttpResponse(error.response))
        .catch(failed(dispatch));

/**
 * What a grant may name, as `{id, title}` — `kind` is the grant's write field.
 * An athlete is read through the profiles, which carry the name.
 */
const TARGET_SOURCES = {
    /*
     * WHICH COMPETITION IS WHICH (organiser, 21 Oct 2026: «не зрозуміло, що
     * за змагання, може хоч добавимо коли вони відбуваються … самі нові
     * вверху»; «додай і місто»): its dates, town and federation under the
     * title, newest first — many titles repeat year after year.
     */
    competition: {
        collection: 'competitions',
        options: rows => rows.map(r => ({
            id: r.id,
            title: r.title,
            subtitle: competitionWhenWhere(r),
            startDate: r.startDate || '',
        })),
        sort: (a, b) => b.startDate.localeCompare(a.startDate) || (a.title || '').localeCompare(b.title || '', 'uk'),
    },
    rule: { collection: 'rules', options: rows => rows.map(r => ({ id: r.id, title: r.title })) },
    competitionFee: { collection: 'competition_fees', options: rows => rows.map(r => ({ id: r.id, title: r.title })) },
    club: { collection: 'clubs', options: rows => rows.map(r => ({ id: r.id, title: r.title })) },
    sportsman: {
        collection: 'profiles',
        options: rows => rows
            .filter(p => p.sportsman && p.sportsman.id)
            .map(p => ({ id: p.sportsman.id, title: [p.lastName, p.firstName, p.middleName].filter(Boolean).join(' ') })),
    },
};

export const ACCESS_GRANT_KINDS = Object.keys(TARGET_SOURCES);

export const fetchAccessGrantTargets = kind => dispatch => {
    const source = TARGET_SOURCES[kind];
    const params = [];
    params['collection'] = source.collection;

    return axios(apiRequestOptions(apiPath.ACCESS_GRANTS.TARGETS, null, { params }))
        .then(transformHttpResponse)
        .then(response => source.options(response.data || [])
            .sort(source.sort || ((a, b) => (a.title || '').localeCompare(b.title || '', 'uk'))))
        .catch(error => transformHttpResponse(error.response))
        .catch(failed(dispatch));
};

/**
 * Whom a grant may name — every account but the super admins', whom no
 * grant concerns. `{id, title}`, the title being the login and the name.
 */
export const fetchGrantableUsers = () => dispatch => {
    const params = [];
    params['collection'] = 'users';

    return axios(apiRequestOptions(apiPath.ACCESS_GRANTS.TARGETS, null, { params }))
        .then(transformHttpResponse)
        .then(response => (response.data || [])
            .filter(u => !(u.roles || []).includes('ROLE_SUPER_ADMIN'))
            .map(u => {
                const p = u.profile || {};
                const name = [p.lastName, p.firstName].filter(Boolean).join(' ');

                return { id: u.id, title: name ? `${u.username} — ${name}` : u.username, disciplines: u.disciplines || [] };
            })
            .sort((a, b) => a.title.localeCompare(b.title, 'uk')))
        .catch(error => transformHttpResponse(error.response))
        .catch(failed(dispatch));
};
